// Frame: Asset Distribution Summary
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { unifiedContactService } from '../../services/unifiedContactService';
import { assetAggregationService, type UnifiedAsset } from '../../services/assetAggregationService.ts';
import { isFullyAssigned } from '../../utils/distributionUtils';
import { FinishFlowAction } from '../../flows/FinishFlowAction';
import { SubHeadline } from '../ui/Typography.tsx';
import { Card, Button } from '../ui';

interface BeneficiaryInfo {
  contactId: string;
  name: string;
  percentage?: number;
}

interface ContactRecord {
  id: string;
  firstName?: string;
  lastName?: string;
}

interface DistributionBeneficiaryRecord {
  beneficiaryId?: string;
  contactId?: string;
  percentage?: number | string;
}

interface DistributionRecord {
  assetId?: string;
  assetCategory?: string;
  primaryBeneficiaries?: DistributionBeneficiaryRecord[];
  contingentBeneficiaries?: DistributionBeneficiaryRecord[];
  primaryBeneficiaryId?: string;
  primaryPercentage?: number | string;
  contingentBeneficiaryId?: string;
  instructions?: string;
  notes?: string;
}

interface ExistingBeneficiaryRecord {
  contactId?: string;
  percentage?: number | string;
}

interface AssetDistribution {
  id: string;
  assetId: string;
  assetName: string;
  assetCategory: string;
  primaryBeneficiaries: BeneficiaryInfo[];
  contingentBeneficiaries: BeneficiaryInfo[];
  instructions?: string;
  notes?: string;
  documentUrl?: string;
  documentName?: string;
}

const Frame247: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useSessionPersistence();
  const [distributions, setDistributions] = useState<AssetDistribution[]>([]);

  useEffect(() => {
    const loadData = async () => {
      // 1. Get raw saved distributions (Record<assetId, DistributionRecord>)
      const rawDistributions = session.taskProgress?.assetDistribution?.answers?.distributions || {};

      // 2. Fetch dependencies to hydrate names
      // Assets
      const assets = assetAggregationService.getAllAssets(session);

      // Contacts (async)
      let contacts: ContactRecord[] = [];
      try {
        contacts = await unifiedContactService.getAllContacts();
        console.log('[Frame247] Loaded contacts:', contacts.length);
      } catch (e) {
        console.error('Failed to load contacts for summary', e);
      }

      // 3. Map and Hydrate manually-assigned distributions
      // Handle both Array (legacy) and Record (new) formats just in case.
      // When reading a Record, use the map key as a fallback for assetId so that
      // records written without an explicit assetId field (e.g. by autoSyncDistributionForAsset
      // before this fix) are still resolved correctly.
      const distArray: DistributionRecord[] = Array.isArray(rawDistributions)
        ? (rawDistributions as DistributionRecord[])
        : Object.entries(rawDistributions as Record<string, DistributionRecord>).map(
            ([k, v]) => ({ ...v, assetId: v.assetId || k })
          );

      // ADDED: Build a contact lookup map keyed by contact ID so hydration below
      // can resolve IDs in O(1) instead of scanning the array on every beneficiary.
      const contactMap = new Map<string, ContactRecord>(contacts.map((c) => [c.id, c]));

      const toPercentage = (value: number | string | undefined, fallback = 100): number => {
        if (typeof value === 'number') return value;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      // ADDED: resolveContact returns a hydrated BeneficiaryInfo when the contact
      // exists in the current contacts list, or null when the contact has been
      // deleted. Callers filter out nulls so deleted contacts are never surfaced
      // as "Unknown Beneficiary" and the asset moves back to Unassigned Assets.
      const resolveContact = (id: string, pct: number): BeneficiaryInfo | null => {
        const contact = contactMap.get(id);
        if (!contact) return null;
        return {
          contactId: id,
          name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
          percentage: pct,
        };
      };

      // Track which asset IDs have manual assignments
      const manuallyAssignedIds = new Set<string>();

      const hydrated = distArray.map((d) => {
        const assetId = d.assetId || '';
        const asset = assets.find((a: UnifiedAsset) => a.id === assetId);

        if (!asset || !assetId) return null;

        manuallyAssignedIds.add(assetId);

        // Safe Defaults
        const assetName = asset.name || 'Unknown Asset';
        const assetCat = d.assetCategory || asset.category || asset.sourceType || 'personal';

        // Hydrate primary beneficiaries — support both new and legacy formats.
        // MODIFIED: map through resolveContact and filter nulls so that any
        // beneficiary whose contact was deleted is silently dropped rather than
        // appearing as "Unknown Beneficiary". An asset left with no valid primary
        // beneficiaries will have an empty array and be caught by flaggedAssets.
        let primaryBeneficiaries: BeneficiaryInfo[] = [];
        if (Array.isArray(d.primaryBeneficiaries) && d.primaryBeneficiaries.length > 0) {
          // New format: array of { beneficiaryId, percentage }
          primaryBeneficiaries = d.primaryBeneficiaries
            .map((pb) => {
              const contactId = pb.beneficiaryId || pb.contactId || '';
              if (!contactId) return null;
              return resolveContact(contactId, toPercentage(pb.percentage));
            })
            .filter((b): b is BeneficiaryInfo => b !== null);
        } else if (typeof d.primaryBeneficiaryId === 'string' && d.primaryBeneficiaryId.length > 0) {
          // Legacy format: single beneficiary fields. Only push if the contact still exists.
          const resolved = resolveContact(d.primaryBeneficiaryId, toPercentage(d.primaryPercentage));
          if (resolved) primaryBeneficiaries = [resolved];
        }

        // Hydrate contingent beneficiaries — support both new and legacy formats.
        // MODIFIED: same deleted-contact filter applied as for primary beneficiaries above.
        let contingentBeneficiaries: BeneficiaryInfo[] = [];
        if (Array.isArray(d.contingentBeneficiaries) && d.contingentBeneficiaries.length > 0) {
          contingentBeneficiaries = d.contingentBeneficiaries
            .map((cb) => {
              const contactId = cb.beneficiaryId || cb.contactId || '';
              if (!contactId) return null;
              return resolveContact(contactId, toPercentage(cb.percentage));
            })
            .filter((b): b is BeneficiaryInfo => b !== null);
        } else if (typeof d.contingentBeneficiaryId === 'string' && d.contingentBeneficiaryId.length > 0) {
          // Legacy format: only push if the contact still exists.
          const resolved = resolveContact(d.contingentBeneficiaryId, 100);
          if (resolved) contingentBeneficiaries = [resolved];
        }

        const item: AssetDistribution = {
          id: assetId,
          assetId,
          assetName: assetName,
          assetCategory: mapCategoryToKey(assetCat),
          primaryBeneficiaries,
          contingentBeneficiaries,
          instructions: d.instructions,
          notes: d.notes,
        };
        return item;
      }).filter((item): item is AssetDistribution => item !== null);

      // 4. Include pre-assigned assets (existingBeneficiaries from upstream)
      //    that were NOT manually overridden in Frame246
      const preAssigned: AssetDistribution[] = assets
        .filter(asset =>
          !manuallyAssignedIds.has(asset.id) &&
          asset.existingBeneficiaries &&
          asset.existingBeneficiaries.length > 0
        )
        .map(asset => ({
          id: asset.id,
          assetId: asset.id,
          assetName: asset.name || 'Unknown Asset',
          assetCategory: mapCategoryToKey(asset.category || asset.sourceType || 'personal'),
          // MODIFIED: filter out pre-assigned beneficiaries whose contact no
          // longer exists, same as manual distributions above. If all entries
          // are removed the asset keeps its distribution object with an empty
          // primaryBeneficiaries array so flaggedAssets picks it up correctly.
          primaryBeneficiaries: ((asset.existingBeneficiaries || []) as ExistingBeneficiaryRecord[])
            .map((eb) => {
              const contactId = eb.contactId;
              if (typeof contactId !== 'string' || contactId.length === 0) return null;
              return resolveContact(contactId, toPercentage(eb.percentage));
            })
            .filter((b): b is BeneficiaryInfo => b !== null),
          contingentBeneficiaries: [],
          instructions: undefined,
          notes: undefined,
        }));

      console.log('[Frame247] Manual distributions:', hydrated.length, '| Pre-assigned:', preAssigned.length);
      setDistributions([...hydrated, ...preAssigned]);
    };

    if (session) {
      loadData();
    }
  }, [session]);

  const mapCategoryToKey = (displayCat: string): string => {
       const lower = (displayCat || '').toLowerCase();
       if (lower.includes('financial')) return 'financial';
       if (lower.includes('real estate') || lower.includes('property')) return 'real_estate';
       if (lower.includes('personal')) return 'personal';
       if (lower.includes('pet')) return 'pets';
       if (lower.includes('digital')) return 'digital';
       if (lower.includes('business')) return 'business';
       return 'personal';
  };

  const categoryLabels: Record<string, string> = {
    real_estate: 'Real Estate Properties',
    financial: 'Financial Accounts',
    personal: 'Personal Belongings',
    digital: 'Digital Assets',
    business: 'Business Interests',
    pets: 'Pets and Dependents'
  };

  // Asset lookup map: id → UnifiedAsset (used for dynamic recipient labels)
  const assetLookup = useMemo(() => {
    const all = assetAggregationService.getAllAssets(session);
    return new Map(all.map(a => [a.id, a]));
  }, [session]);

  // Compute flaggedAssets - assets needing attention (no beneficiaries OR incomplete %)
  const flaggedAssets = useMemo(() => {
    const result: { id: string; name: string; category: string; reason: string }[] = [];
    const allAssets = assetAggregationService.getAllAssets(session);

    for (const asset of allAssets) {
      const dist = distributions.find(d => d.assetId === asset.id);
      const rLabelPlural =
        asset.recipientLabel === 'Intended Recipient' ? 'intended recipients' : 'beneficiaries';
      if (!dist || !dist.primaryBeneficiaries || dist.primaryBeneficiaries.length === 0) {
        result.push({
          id: asset.id,
          name: asset.name || 'Unknown Asset',
          category: asset.sourceType || 'personal',
          reason: `No ${rLabelPlural} assigned`
        });
      } else {
        const total = dist.primaryBeneficiaries.reduce((sum, b) => sum + (Number(b.percentage) || 0), 0);
        if (total !== 100) {
          result.push({
            id: asset.id,
            name: asset.name || 'Unknown Asset',
            category: asset.sourceType || 'personal',
            reason: `${total}% assigned — 100% required`
          });
        }
      }
    }
    return result;
  }, [distributions, session]);

  // Only include fully assigned assets
  const assignedDistributions = useMemo(() =>
    distributions.filter(d => isFullyAssigned(d)),
    [distributions]
  );

  // Split assigned distributions by relationship type using the asset lookup built above.
  // Distributions for assets with recipientLabel 'Intended Recipient' (e.g. income sources)
  // are shown in a separate section from traditional beneficiary assets.
  const beneficiaryAssigned = assignedDistributions.filter(
    (d) => (assetLookup.get(d.assetId)?.recipientLabel || 'Beneficiary') === 'Beneficiary'
  );
  const intendedRecipientAssigned = assignedDistributions.filter(
    (d) => assetLookup.get(d.assetId)?.recipientLabel === 'Intended Recipient'
  );

  // Group each set by person name (same logic as the original single grouping)
  const distributionsByBeneficiary = beneficiaryAssigned.reduce((acc, dist) => {
    for (const pb of dist.primaryBeneficiaries) {
      if (!acc[pb.name]) acc[pb.name] = [];
      acc[pb.name].push(dist);
    }
    return acc;
  }, {} as Record<string, AssetDistribution[]>);

  const distributionsByIntendedRecipient = intendedRecipientAssigned.reduce((acc, dist) => {
    for (const pb of dist.primaryBeneficiaries) {
      if (!acc[pb.name]) acc[pb.name] = [];
      acc[pb.name].push(dist);
    }
    return acc;
  }, {} as Record<string, AssetDistribution[]>);

  // Group distributions by category
  const distributionsByCategory = assignedDistributions.reduce((acc, dist) => {
    const category = dist.assetCategory;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(dist);
    return acc;
  }, {} as Record<string, AssetDistribution[]>);

  const handleEdit = () => {
    navigate('/frame-246');
  };

  const handleBack = () => {
    navigate('/frame-246');
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <div className="space-y-6">
          <ResponsiveHeader title="Asset Distribution Summary" />

          <SubHeadline>
            Review and confirm how your assets will be distributed among your beneficiaries.
          </SubHeadline>

          {/* Unassigned / Incomplete Assets Warning */}
          {flaggedAssets.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <div className="flex justify-between items-start mb-3">
                <h2 className="text-lg font-semibold text-orange-800">Unassigned Assets</h2>
                <button
                  onClick={handleEdit}
                  className="px-4 py-1.5 bg-orange-100 text-orange-700 text-sm font-medium rounded-md hover:bg-orange-200 transition-colors border border-orange-300"
                >
                  Edit
                </button>
              </div>
              <p className="text-sm text-orange-700 mb-3">
                The following {flaggedAssets.length === 1 ? 'asset needs' : `${flaggedAssets.length} assets need`} attention:
              </p>
              <ul className="space-y-1">
                {flaggedAssets.map(asset => (
                  <li key={asset.id} className="text-sm text-orange-800 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-orange-400 rounded-full flex-shrink-0" />
                    <span>{asset.name}</span>
                    <span className="text-orange-500 text-xs">({asset.reason})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Overview Stats Section */}
          <Card>
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Distribution Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center border-r border-stone-100 last:border-0">
                <div className="text-2xl font-bold text-cyan-900">{assignedDistributions.length}</div>
                <div className="text-sm text-stone-600 font-medium">Assets Assigned</div>
              </div>
              <div className="text-center border-r border-stone-100 last:border-0">
                <div className="text-2xl font-bold text-green-700">
                  {Object.keys(distributionsByBeneficiary).length}
                </div>
                <div className="text-sm text-stone-600 font-medium">Beneficiaries</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-700">
                  {Object.keys(distributionsByCategory).length}
                </div>
                <div className="text-sm text-stone-600 font-medium">Categories</div>
              </div>
            </div>
          </Card>

          {/* By Beneficiary Section */}
          <Card>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-stone-800">By Beneficiary</h3>
                <span className="text-sm text-stone-500">({Object.keys(distributionsByBeneficiary).length})</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleEdit}>
                Edit
              </Button>
            </div>

            <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {beneficiaryAssigned.length > 0 ? (
                <div className="space-y-8">
                  {Object.entries(distributionsByBeneficiary).map(([beneficiaryName, dists]) => (
                    <div key={beneficiaryName}>
                      <h4 className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-3 border-b border-stone-100 pb-1">
                        {beneficiaryName}
                      </h4>
                      <div className="space-y-3">
                        {dists.map((dist) => (
                          <div key={dist.id} className="bg-stone-50 rounded-lg p-4">
                            <div className="font-medium text-stone-800">{dist.assetName}</div>
                            <div className="text-sm text-stone-600 mt-1 capitalize">
                              {categoryLabels[dist.assetCategory] || dist.assetCategory}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No assets assigned to beneficiaries yet.</p>
                </div>
              )}
            </div>
          </Card>

          {/* By Intended Recipient Section — only rendered when income sources or other
              executor-guidance assets have been assigned, keeping the UI uncluttered
              for users who have no such records. */}
          {intendedRecipientAssigned.length > 0 && (
            <Card>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-stone-800">By Intended Recipient</h3>
                  <span className="text-sm text-stone-500">({Object.keys(distributionsByIntendedRecipient).length})</span>
                </div>
                <Button variant="outline" size="sm" onClick={handleEdit}>
                  Edit
                </Button>
              </div>

              <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                <div className="space-y-8">
                  {Object.entries(distributionsByIntendedRecipient).map(([recipientName, dists]) => (
                    <div key={recipientName}>
                      <h4 className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-3 border-b border-stone-100 pb-1">
                        {recipientName}
                      </h4>
                      <div className="space-y-3">
                        {dists.map((dist) => (
                          <div key={dist.id} className="bg-stone-50 rounded-lg p-4">
                            <div className="font-medium text-stone-800">{dist.assetName}</div>
                            <div className="text-sm text-stone-600 mt-1 capitalize">
                              {categoryLabels[dist.assetCategory] || dist.assetCategory}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* By Category Section */}
          <Card>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-stone-800">By Category</h3>
                <span className="text-sm text-stone-500">({Object.keys(distributionsByCategory).length})</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleEdit}>
                Edit
              </Button>
            </div>

            <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {assignedDistributions.length > 0 ? (
                <div className="space-y-8">
                  {Object.entries(distributionsByCategory).map(([category, dists]) => (
                    <div key={category}>
                      <h4 className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-3 border-b border-stone-100 pb-1">
                        {categoryLabels[category] || category}
                      </h4>
                      <div className="space-y-3">
                        {dists.map((dist) => {
                          const distAsset = assetLookup.get(dist.assetId);
                          const rLabel = distAsset?.recipientLabel || 'Beneficiary';
                          return (
                            <div key={dist.id} className="bg-stone-50 rounded-lg p-4">
                              <div className="font-medium text-stone-800">{dist.assetName}</div>
                              <div className="text-sm text-stone-600 mt-1">
                                {rLabel}: {dist.primaryBeneficiaries.map((pb, i) => (
                                  <span key={pb.contactId}>{i > 0 ? ', ' : ''}{pb.name} ({pb.percentage}%)</span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No assets categorized yet.</p>
                </div>
              )}
            </div>
          </Card>

          {/* Legal Notice Footer */}
          <div className="max-w-4xl">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-6">
              <h3 className="text-sm font-bold text-teal-800 uppercase tracking-tight mb-2">Important Legal Notice</h3>
              <div className="text-sm text-teal-700 leading-relaxed space-y-4">
                <strong>Beneficiary Designations:</strong> Assets with a named beneficiary (such as life insurance or retirement accounts) generally transfer directly to the listed beneficiary and may bypass probate, depending on account rules and applicable laws.
                <br /><br />
                <strong>Executor Guidance:</strong> Assets labeled with an “Intended Recipient” represent your wishes for how items should be distributed. These instructions serve as guidance for your executor and may only be legally binding if they are included in a valid estate planning document such as a Last Will & Testament or trust.
              </div>
            </div>
          </div>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Asset Distribution Summary"
        subtitle="Review and confirm your distribution plan"
        progressPercent={100}
        onBack={handleBack}
        backText="Back"
        nextNode={
          <FinishFlowAction
            flowId="distribution"
            onFinished={() => navigate('/frame-140')}
            buttonText="Continue to Dashboard"
            className="!bg-cyan-700 hover:!bg-cyan-800 rounded-sm"
          />
        }
      />
    </ResponsiveContainer>
  );
};

export default Frame247;